import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useParams, useOutletContext } from "react-router-dom";
import { useSelector } from "react-redux";

import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  FileText,
  LayoutDashboard,
  MoreHorizontal,
  TrendingUp,
  Network,
  Loader2,
  PencilRuler,
  BookOpen,
} from "lucide-react";
import { TacoAssetType, CANONICAL_ASSET_PATHS } from "@amzn/afxtaco-types";
import { reducerType } from "../../redux/reducers";
import { isExecutionActive } from "../../redux/reducers/Execution";
import { useProjects } from "../hooks/useProjects";
import { useProjectAsset } from "../hooks/useProjectAsset";
import { useAdditionalAssets } from "../hooks/useAdditionalAssets";
import { useFeatureFlags } from "../utils/FeatureFlagDetection";
import { ThinkingTabContent } from "./thinking";
import MarkdownRenderer from "./MarkdownRenderer";
import DrawioEditor from "./drawio/DrawioEditor";
import { BlogPostTabContent } from "./blogpost";
import {
  AdditionalAssetTab,
  AdditionalAssetTabsBar,
  NewContentDot,
  additionalAssetLabel,
  additionalTabValue,
  TAB_TRIGGER_CLASS,
} from "./additionalAssets";
import { HelpPanel } from "./helpPanel";
import { OverviewTab, type CanonicalDeliverableSpec } from "./overview";

enum ProjectTab {
  OVERVIEW = "overview",
  SUMMARY = "summary",
  ARCHITECTURE = "architecture",
  DRAWIO = "drawio",
  IMPLEMENTATION = "implementation",
  THINKING = "thinking",
  BLOG_POST = "blogPost",
}

// Tabs the full-pane "Generating…" spinner must never replace. Both show live
// progress themselves, so blanking them would hide exactly what the user came
// for. THINKING stays listed harmlessly for when that tab is removed.
const TABS_EXEMPT_FROM_GENERATING_SPINNER: ReadonlySet<string> = new Set([
  ProjectTab.OVERVIEW,
  ProjectTab.THINKING,
]);

// Maps a canonical/known manifest path to the fixed tab it backs, so a changed
// asset dots the right tab. Additional assets are handled separately (their tab
// value is derived from the path). Paths not present here (e.g. the diagram
// baseline) simply don't map to a tab and raise no dot.
const PATH_TO_FIXED_TAB: Record<string, ProjectTab> = {
  [CANONICAL_ASSET_PATHS[TacoAssetType.ARCHITECTURE_SUMMARY]]:
    ProjectTab.SUMMARY,
  [CANONICAL_ASSET_PATHS[TacoAssetType.IMPLEMENTATION_DETAILS]]:
    ProjectTab.IMPLEMENTATION,
  [CANONICAL_ASSET_PATHS[TacoAssetType.BIDI_DIAGRAM]]: ProjectTab.DRAWIO,
  [CANONICAL_ASSET_PATHS[TacoAssetType.BLOG_POST]]: ProjectTab.BLOG_POST,
};

/** The persisted activity log. Has no tab of its own; see below. */
const ACTIVITY_LOG_PATH = "thinking/thinking.md";

/**
 * Resolves the fixed tab an updated asset should dot.
 *
 * The activity log is the reason this isn't a plain lookup: it is the only asset
 * whose tab is conditional. The Thinking tab renders it when shown; nothing
 * displays it otherwise, so it must raise no dot at all rather than mark a tab
 * that either isn't rendered or doesn't show that content.
 *
 * Returns undefined for paths with no fixed tab (e.g. the diagram baseline), so
 * the caller can fall back to additional-asset tab values.
 *
 * Exported for tests: the dot it drives renders as an unlabelled span, so the
 * conditional mapping is asserted here rather than through the DOM.
 */
export const fixedTabForPath = (
  path: string,
  showThinkingTab: boolean,
): ProjectTab | undefined => {
  if (path === ACTIVITY_LOG_PATH) {
    return showThinkingTab ? ProjectTab.THINKING : undefined;
  }
  return PATH_TO_FIXED_TAB[path];
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const ArchitectureComposerPage = () => {
  const { projectId } = useParams();

  // Feature flags: control tab visibility. Read before the tab state below
  // because the Overview flag decides the landing tab. Safe to rely on here —
  // AuthenticationWrapper blocks all rendering until flags have loaded.
  const {
    isBlogPostEnabled,
    isAdditionalAssetsEnabled,
    isThinkingChevronEnabled,
    isOverviewPageEnabled,
  } = useFeatureFlags([
    "blogPost",
    "additionalAssets",
    "thinkingChevron",
    "overviewPage",
  ]);

  const defaultTab = isOverviewPageEnabled
    ? ProjectTab.OVERVIEW
    : ProjectTab.SUMMARY;

  // `activeTab` holds either a fixed ProjectTab value or an additional-asset tab
  // value (namespaced by ADDITIONAL_TAB_PREFIX), so it's typed as a string.
  const [activeTab, setActiveTab] = useState<string>(defaultTab);

  const [prevActiveTab, setPrevActiveTab] = useState<string>(defaultTab);

  const { getProjectById, fetchProject, isLoading } = useProjects();
  const fetchAttempted = useRef(false);
  const [fetchError, setFetchError] = useState(false);
  const currentProject = projectId ? getProjectById(projectId) : null;

  const executionStatus = useSelector(
    (state: reducerType) => state.Execution.status,
  );
  const executionProjectId = useSelector(
    (state: reducerType) => state.Execution.projectId,
  );
  const assetUpdates = useSelector(
    (state: reducerType) => state.Execution.assetUpdates,
  );

  // Redux-backed new-project flag (see Execution slice). Scoped to the
  // current route's projectId so a stale flag from a different project's
  // execution can't leak in.
  const isNewProjectFlowRedux = useSelector(
    (state: reducerType) => state.Execution.isNewProjectFlow,
  );
  const isNewProjectFlow =
    isNewProjectFlowRedux && executionProjectId === projectId;

  // System events for the Thinking tab (tool calls, thoughts, completions).
  // Only show events that belong to the currently viewed project.
  const allSystemEvents = useSelector(
    (state: reducerType) => state.Execution.systemEvents,
  );
  const systemEvents = executionProjectId === projectId ? allSystemEvents : [];

  // `thinkingChevron` folds the Thinking tab's tool timeline into the chat panel
  // as inline chevrons, so the tab itself is hidden. Hidden only — the tab's
  // component, its S3 persistence, and the backend events all stay in place so
  // turning the flag off restores the previous behavior.
  const showThinkingTab = !isThinkingChevronEnabled;

  // Canonical artifacts the Overview lists, in tab-bar order. Declared here
  // rather than inside the feature because tab identifiers live in this file and
  // Blog Post is feature-flagged. Memoised so it doesn't re-derive every render.
  //
  // Each spec carries the same glyph as its TabsTrigger below, so a Deliverables
  // row is recognisable as the tab it opens. Kept in this file for that reason —
  // the icon and the trigger have to be changed together or they drift.
  const canonicalDeliverableSpecs: CanonicalDeliverableSpec[] = useMemo(
    () => [
      {
        assetType: TacoAssetType.ARCHITECTURE_SUMMARY,
        label: "Summary",
        tabValue: ProjectTab.SUMMARY,
        icon: <FileText className="h-4 w-4" />,
      },
      {
        assetType: TacoAssetType.BIDI_DIAGRAM,
        label: "Architecture Diagram",
        tabValue: ProjectTab.DRAWIO,
        icon: <PencilRuler className="h-4 w-4" />,
      },
      {
        assetType: TacoAssetType.IMPLEMENTATION_DETAILS,
        label: "Implementation",
        tabValue: ProjectTab.IMPLEMENTATION,
        icon: <TrendingUp className="h-4 w-4" />,
      },
      ...(isBlogPostEnabled
        ? [
            {
              assetType: TacoAssetType.BLOG_POST,
              label: "Blog Post",
              tabValue: ProjectTab.BLOG_POST as string,
              icon: <BookOpen className="h-4 w-4" />,
            },
          ]
        : []),
    ],
    [isBlogPostEnabled],
  );

  // Whether the agent is actively processing this project
  const isThisProjectExecuting =
    executionProjectId === projectId && isExecutionActive(executionStatus);

  // Show the generating spinner only when the backend is actively processing
  // this project AND has signalled that assets are being generated.
  // Chat-only executions never dispatch asset_update messages, so the spinner
  // won't appear for them — preventing the misleading "Generating architecture
  // diagram…" message on plain chat interactions.
  const isGenerating =
    isThisProjectExecuting &&
    assetUpdates.length > 0 &&
    !TABS_EXEMPT_FROM_GENERATING_SPINNER.has(activeTab);

  // The backend doesn't reliably send asset_update WebSocket messages, but
  // the completion thunk bumps assetsStaleCounter when new assets are written.
  // We use that counter as the signal: when it increments, every tab except
  // the one the user is currently viewing gets a blue dot.
  const staleCounter = useSelector((state: reducerType) =>
    projectId ? (state.Projects.assetsStaleCounter[projectId] ?? 0) : 0,
  );

  const [tabsWithNewContent, setTabsWithNewContent] = useState<Set<string>>(
    new Set(),
  );
  const prevStaleCounterRef = useRef(staleCounter);

  // Reset indicators on project change only (ref guards against staleCounter re-triggers).
  const prevProjectIdRef = useRef(projectId);
  // Tracks whether we've already auto-switched to the Thinking tab for this project.
  // Reset on project boundary transitions so a second new-project creation also auto-switches.
  const hasAutoSwitchedToThinkingRef = useRef(false);
  useEffect(() => {
    if (prevProjectIdRef.current !== projectId) {
      setTabsWithNewContent(new Set());
      prevStaleCounterRef.current = staleCounter;
      prevProjectIdRef.current = projectId;
      hasAutoSwitchedToThinkingRef.current = false;
      hasAutoOpenedHelpRef.current = false;
    }
  }, [projectId, staleCounter]);

  // Help panel state: shared from IDELayout via Outlet context.
  const { isHelpPanelOpen, setIsHelpPanelOpen, expandChatPanel } =
    useOutletContext<{
      isHelpPanelOpen: boolean;
      setIsHelpPanelOpen: React.Dispatch<React.SetStateAction<boolean>>;
      expandChatPanel?: () => void;
    }>();
  const hasAutoOpenedHelpRef = useRef(false);

  // Auto-open help panel on new project flow
  useEffect(() => {
    if (isNewProjectFlow && !hasAutoOpenedHelpRef.current) {
      hasAutoOpenedHelpRef.current = true;
      setIsHelpPanelOpen(true);
    }
  }, [isNewProjectFlow, setIsHelpPanelOpen]);

  // Callback that sets the active tab and clears its indicator. `value` may be a
  // fixed ProjectTab or an additional-asset tab value (path-namespaced).
  const handleTabChange = useCallback((value: string) => {
    setTabsWithNewContent((prev) => {
      if (!prev.has(value)) return prev;
      const next = new Set(prev);
      next.delete(value);
      return next;
    });
    setActiveTab(value);
  }, []);

  // Populates the chat input with `text` and hands the user to it. Never sends —
  // submitting stays a deliberate user action, because a turn is expensive and
  // the text is a starting point they should be able to edit.
  //
  // Uses ChatInput's existing `insertPrompt` window event rather than the send
  // pipeline: `useSendChatMessage` needs the chat panel's whole state (nav refs,
  // streaming refs, conversation id, message count), so driving it from here
  // would mean a second copy of that setup.
  //
  // ChatInput owns focus, caret placement and the landing highlight on insert;
  // this only expands the panel first (a prefill into a collapsed 64px rail
  // would be invisible) and focuses directly for the no-text case.
  const prefillChat = useCallback(
    (text: string) => {
      expandChatPanel?.();

      // Deferred a frame, because `IDEChatPanel` renders a collapsed rail instead
      // of the real panel, so `ChatInput` is unmounted while collapsed. Expanding
      // only schedules a re-render; dispatching in the same tick fired the event
      // before the listener existed, and the text was silently dropped. The same
      // applies to the focus path, whose selector would find nothing.
      requestAnimationFrame(() => {
        if (text) {
          window.dispatchEvent(
            new CustomEvent("insertPrompt", { detail: { text } }),
          );
          return;
        }

        document
          .querySelector<HTMLTextAreaElement>("[data-taco-chat-input]")
          ?.focus();
      });
    },
    [expandChatPanel],
  );

  // Auto-switch to Thinking on the new-project flow only. Skipped when the
  // Thinking tab is hidden — with chevrons the activity is already visible in
  // the chat panel, so there is nothing to switch to. Also skipped when Overview
  // is enabled: Overview is the landing tab and reports progress itself, so
  // yanking the user off it would defeat the point.
  useEffect(() => {
    if (
      showThinkingTab &&
      !isOverviewPageEnabled &&
      isNewProjectFlow &&
      !hasAutoSwitchedToThinkingRef.current
    ) {
      hasAutoSwitchedToThinkingRef.current = true;
      handleTabChange(ProjectTab.THINKING);
    }
  }, [
    showThinkingTab,
    isOverviewPageEnabled,
    isNewProjectFlow,
    handleTabChange,
  ]);

  // Skip asset fetches only during the new-project flow (assets don't
  // exist in S3 yet). After refresh the flag is false, so fetches run.
  const skipFetch = isNewProjectFlow;

  // One hook call per asset type — handles fetch, retry, and cleanup internally
  const summary = useProjectAsset({
    projectId,
    assetType: TacoAssetType.ARCHITECTURE_SUMMARY,
    skip: skipFetch,
  });
  const implementation = useProjectAsset({
    projectId,
    assetType: TacoAssetType.IMPLEMENTATION_DETAILS,
    skip: skipFetch,
  });
  const thinking = useProjectAsset({
    projectId,
    assetType: TacoAssetType.THINKING,
    skip: skipFetch,
  });
  const blogPost = useProjectAsset({
    projectId,
    assetType: TacoAssetType.BLOG_POST,
    skip: skipFetch || !isBlogPostEnabled,
  });
  // Structured definition of done behind the Overview's checklist. Only fetched
  // when that tab exists, since nothing else reads it.
  const deliveryDefinition = useProjectAsset({
    projectId,
    assetType: TacoAssetType.DELIVERY_DEFINITION,
    skip: skipFetch || !isOverviewPageEnabled,
  });

  // Additional (non-canonical) assets — gated behind the additionalAssets flag.
  // The inline vs. "More" overflow split is handled by AdditionalAssetTabsBar
  // (width-based), so the page just passes the full list through.
  const { assets: additionalAssets } = useAdditionalAssets({
    projectId,
    skip: skipFetch || !isAdditionalAssetsEnabled,
  });

  // Per-asset "new content" dots are driven by the agent's `asset_update`
  // websocket events (accumulated in Execution.assetUpdates during a turn). Each
  // event carries the changed asset's `path`, which we map to its fixed or
  // additional-asset tab — dotting ONLY the tabs whose asset actually changed,
  // excluding the tab the user is currently viewing. Only newly-arrived updates
  // are processed (tracked by count), so re-renders don't re-dot.
  const processedAssetUpdateCountRef = useRef(0);
  useEffect(() => {
    // assetUpdates is cleared at turn start (startExecution), so a shrink means
    // a new turn began — reset our processed cursor to consume from the top.
    if (assetUpdates.length < processedAssetUpdateCountRef.current) {
      processedAssetUpdateCountRef.current = 0;
    }
    if (assetUpdates.length === processedAssetUpdateCountRef.current) return;

    const fresh = assetUpdates.slice(processedAssetUpdateCountRef.current);
    processedAssetUpdateCountRef.current = assetUpdates.length;

    setTabsWithNewContent((prev) => {
      const next = new Set(prev);
      for (const update of fresh) {
        const fixed = fixedTabForPath(update.path, showThinkingTab);
        // An asset with no fixed tab and no additional-asset tab raises no dot.
        if (fixed === undefined && update.path === ACTIVITY_LOG_PATH) continue;
        const tabValue = fixed ?? additionalTabValue(update.path);
        if (tabValue !== activeTab) next.add(tabValue);
      }
      return next;
    });
  }, [assetUpdates, activeTab, showThinkingTab]);

  // Reset fetch guard when the projectId changes
  useEffect(() => {
    fetchAttempted.current = false;
    setFetchError(false);
  }, [projectId]);

  // Only `getProject` returns the latest session's fields; the projects-list
  // response does not. So a record already in Redux because the user came from
  // the projects list is missing them, and the Overview's "Last activity" — which
  // reads `latestSessionLastActivityAt` — would have nothing to show.
  //
  // `latestSessionId` is the marker for "hydrated by getProject". A project that
  // genuinely has no session leaves it undefined, so this can ask for one refetch
  // that adds nothing; the `fetchAttempted` guard caps that at once per project.
  const needsSessionDetail = Boolean(
    isOverviewPageEnabled &&
    currentProject &&
    currentProject.latestSessionId === undefined,
  );

  // Fetch project data if not already loaded. Skipped during the
  // new-project flow (project record doesn't exist server-side yet).
  useEffect(() => {
    if (
      projectId &&
      !isNewProjectFlow &&
      (!currentProject || needsSessionDetail) &&
      !isLoading &&
      !fetchAttempted.current
    ) {
      fetchAttempted.current = true;
      fetchProject(projectId).catch(() => {
        setFetchError(true);
      });
    }
  }, [
    projectId,
    isNewProjectFlow,
    currentProject,
    needsSessionDetail,
    isLoading,
    fetchProject,
  ]);

  // Retry fetching assets when user navigates to a tab after a prior 404
  useEffect(() => {
    if (activeTab !== prevActiveTab) {
      if (activeTab === ProjectTab.SUMMARY && summary.isNotFound) {
        summary.retry();
      }
      if (
        activeTab === ProjectTab.IMPLEMENTATION &&
        implementation.isNotFound
      ) {
        implementation.retry();
      }
      if (activeTab === ProjectTab.THINKING && thinking.isNotFound) {
        thinking.retry();
      }
      if (activeTab === ProjectTab.BLOG_POST && blogPost.isNotFound) {
        blogPost.retry();
      }
      setPrevActiveTab(activeTab);
    }
  }, [activeTab, prevActiveTab, summary, implementation, thinking, blogPost]);

  return (
    <div className="h-full flex flex-col bg-background">
      {/* Project not found / malformed ID error state */}
      {fetchError && !currentProject && (
        <div className="flex-1 flex items-center justify-center bg-background">
          <div className="flex flex-col items-center gap-2 text-center">
            <p className="text-sm font-medium text-destructive">
              Project not found
            </p>
            <p className="text-xs text-muted-foreground">
              The project ID in the URL may be invalid or the project may have
              been deleted.
            </p>
          </div>
        </div>
      )}
      {/* Document Content */}
      {(!fetchError || currentProject) && (
        <div className="flex-1 overflow-hidden bg-background">
          <Tabs
            value={activeTab}
            onValueChange={handleTabChange}
            className="h-full flex flex-col bg-background"
          >
            <div className="border-b border-border">
              <TabsList className="relative flex w-full justify-start h-auto p-0 bg-transparent">
                {isOverviewPageEnabled && (
                  <TabsTrigger
                    value={ProjectTab.OVERVIEW}
                    className={TAB_TRIGGER_CLASS}
                  >
                    <LayoutDashboard className="h-4 w-4" />
                    <span>Overview</span>
                    {tabsWithNewContent.has(ProjectTab.OVERVIEW) && (
                      <NewContentDot />
                    )}
                  </TabsTrigger>
                )}
                <TabsTrigger
                  value={ProjectTab.SUMMARY}
                  className={TAB_TRIGGER_CLASS}
                >
                  <FileText className="h-4 w-4" />
                  <span>Summary</span>
                  {tabsWithNewContent.has(ProjectTab.SUMMARY) && (
                    <NewContentDot />
                  )}
                </TabsTrigger>
                <TabsTrigger value={ProjectTab.ARCHITECTURE} className="hidden">
                  <Network className="h-4 w-4" />
                  <span>Architecture Diagram</span>
                  {tabsWithNewContent.has(ProjectTab.ARCHITECTURE) && (
                    <NewContentDot />
                  )}
                </TabsTrigger>
                <TabsTrigger
                  value={ProjectTab.DRAWIO}
                  className={TAB_TRIGGER_CLASS}
                >
                  <PencilRuler className="h-4 w-4" />
                  <span>Architecture Diagram</span>
                  {tabsWithNewContent.has(ProjectTab.DRAWIO) && (
                    <NewContentDot />
                  )}
                </TabsTrigger>
                <TabsTrigger
                  value={ProjectTab.IMPLEMENTATION}
                  className={TAB_TRIGGER_CLASS}
                >
                  <TrendingUp className="h-4 w-4" />
                  <span>Implementation</span>
                  {tabsWithNewContent.has(ProjectTab.IMPLEMENTATION) && (
                    <NewContentDot />
                  )}
                </TabsTrigger>
                {isBlogPostEnabled && (
                  <TabsTrigger
                    value={ProjectTab.BLOG_POST}
                    className={TAB_TRIGGER_CLASS}
                  >
                    <BookOpen className="h-4 w-4" />
                    <span>Blog Post</span>
                    {tabsWithNewContent.has(ProjectTab.BLOG_POST) && (
                      <NewContentDot />
                    )}
                  </TabsTrigger>
                )}
                {showThinkingTab && (
                  <TabsTrigger
                    value={ProjectTab.THINKING}
                    className={TAB_TRIGGER_CLASS}
                  >
                    <MoreHorizontal className="h-4 w-4" />
                    <span>Thinking</span>
                    {isThisProjectExecuting ? (
                      <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                    ) : tabsWithNewContent.has(ProjectTab.THINKING) ? (
                      <NewContentDot />
                    ) : null}
                  </TabsTrigger>
                )}
                {/* Additional-asset tabs trail the fixed tabs; the bar's
                    flex-1 growth leaves empty space at the right edge. */}
                {isAdditionalAssetsEnabled && (
                  <AdditionalAssetTabsBar
                    assets={additionalAssets}
                    tabsWithNewContent={tabsWithNewContent}
                    onSelect={handleTabChange}
                  />
                )}
              </TabsList>
            </div>

            <div className="flex-1 overflow-hidden bg-background flex">
              <div className="flex-1 overflow-hidden bg-background">
                {isGenerating ? (
                  <div className="h-full flex items-center justify-center bg-background">
                    <div className="flex flex-col items-center gap-4">
                      <Loader2 className="h-12 w-12 animate-spin text-blue-600" />
                      <p className="text-sm text-muted-foreground">
                        Generating architecture diagram...
                      </p>
                    </div>
                  </div>
                ) : (
                  <>
                    {isOverviewPageEnabled && (
                      <TabsContent
                        value={ProjectTab.OVERVIEW}
                        className="h-full m-0 bg-background"
                      >
                        <OverviewTab
                          projectId={projectId}
                          project={currentProject ?? undefined}
                          canonicalSpecs={canonicalDeliverableSpecs}
                          additionalAssets={additionalAssets}
                          additionalLabel={additionalAssetLabel}
                          additionalTabValue={additionalTabValue}
                          summaryContent={summary.content}
                          deliveryDefinitionContent={deliveryDefinition.content}
                          onOpenTab={handleTabChange}
                          onPrefillChat={prefillChat}
                          skip={skipFetch}
                        />
                      </TabsContent>
                    )}

                    {/* ARCHITECTURE tab deprecated — static PNG viewer removed */}

                    <TabsContent
                      value={ProjectTab.DRAWIO}
                      className="h-full m-0 bg-background"
                    >
                      {projectId && <DrawioEditor projectId={projectId} />}
                    </TabsContent>

                    <TabsContent
                      value={ProjectTab.SUMMARY}
                      className="h-full m-0 bg-background"
                    >
                      <div className="h-full overflow-y-auto bg-background">
                        <div className="p-6 bg-background">
                          {summary.isLoading ? (
                            <div className="flex items-center justify-center py-12">
                              <div className="flex flex-col items-center gap-4">
                                <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                                <p className="text-sm text-muted-foreground">
                                  Loading summary...
                                </p>
                              </div>
                            </div>
                          ) : summary.content ? (
                            <MarkdownRenderer
                              content={summary.content}
                              projectId={projectId}
                            />
                          ) : (
                            <div className="flex items-center justify-center py-12">
                              <p className="text-sm text-muted-foreground">
                                No summary available
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </TabsContent>

                    <TabsContent
                      value={ProjectTab.IMPLEMENTATION}
                      className="h-full m-0 bg-background"
                    >
                      <div className="h-full overflow-y-auto bg-background">
                        <div className="p-6 bg-background">
                          {implementation.isLoading ? (
                            <div className="flex items-center justify-center py-12">
                              <div className="flex flex-col items-center gap-4">
                                <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                                <p className="text-sm text-muted-foreground">
                                  Loading implementation details...
                                </p>
                              </div>
                            </div>
                          ) : implementation.content ? (
                            <MarkdownRenderer
                              content={implementation.content}
                              projectId={projectId}
                            />
                          ) : (
                            <div className="flex items-center justify-center py-12">
                              <p className="text-sm text-muted-foreground">
                                No implementation details available
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </TabsContent>

                    {isBlogPostEnabled && (
                      <TabsContent
                        value={ProjectTab.BLOG_POST}
                        className="h-full m-0 bg-background"
                      >
                        {projectId && (
                          <BlogPostTabContent
                            blogPost={blogPost}
                            projectId={projectId}
                          />
                        )}
                      </TabsContent>
                    )}

                    {showThinkingTab && (
                      <TabsContent
                        value={ProjectTab.THINKING}
                        className="h-full m-0 bg-background"
                      >
                        <ThinkingTabContent
                          systemEvents={systemEvents}
                          isThisProjectExecuting={isThisProjectExecuting}
                          thinking={thinking}
                        />
                      </TabsContent>
                    )}

                    {isAdditionalAssetsEnabled &&
                      projectId &&
                      additionalAssets.map((asset) => (
                        <TabsContent
                          key={additionalTabValue(asset.path)}
                          value={additionalTabValue(asset.path)}
                          className="h-full m-0 bg-background"
                        >
                          <AdditionalAssetTab
                            projectId={projectId}
                            asset={asset}
                          />
                        </TabsContent>
                      ))}
                  </>
                )}
              </div>
              {/* Help Panel — pushes tab content when open */}
              <div
                className="flex-shrink-0 overflow-hidden transition-all duration-300"
                style={{ width: isHelpPanelOpen ? 600 : 0 }}
              >
                <HelpPanel
                  isOpen={isHelpPanelOpen}
                  onClose={() => setIsHelpPanelOpen(false)}
                />
              </div>
            </div>
          </Tabs>
        </div>
      )}
    </div>
  );
};

export default ArchitectureComposerPage;
